import { Fragment, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, Undo2Icon } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';
import { TableCell, TableRow } from '@nesso/ui/primitives/table';

import {
    formatPaymentMethod,
    formatRefundNumber,
    type Payment,
    type Refund,
} from '@/entities/payment';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericDate, formatTime } from '@/shared/lib/time-utils';

import { ClientBillingSheetActivityRefundDetails } from './client-billing-sheet-activity-refund/client-billing-sheet-activity-refund-details';

type Props = {
    refund: Refund;
    isCompact: boolean;
    onOpenOriginalPayment: (payment: Payment) => void;
};

export function ClientBillingSheetActivityRefund({
    refund,
    isCompact,
    onOpenOriginalPayment,
}: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const number = formatRefundNumber(refund.number);

    return (
        <Fragment>
            <TableRow className="h-18">
                <TableCell>
                    <div className="flex flex-col">
                        <span className="font-semibold">
                            {formatNumericDate(refund.refundedAt)}
                        </span>
                        <span className="text-muted-foreground">
                            {formatTime(refund.refundedAt)}
                        </span>
                    </div>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-1.5">
                        <Undo2Icon className="size-4.5 shrink-0" />
                        {number}
                    </div>
                </TableCell>
                {!isCompact && (
                    <>
                        <TableCell>
                            {formatPaymentMethod(refund.method, refund.checkNumber)}
                        </TableCell>
                        <TableCell />
                    </>
                )}
                <TableCell className="text-right">
                    {formatCurrencyFromCents(-refund.amountCents)}
                </TableCell>
                <TableCell className="py-2 pr-1 pl-0.5 text-right">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${number}`}
                        onClick={() => setIsExpanded((expanded) => !expanded)}
                    >
                        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Button>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow variant="detail">
                    <TableCell colSpan={isCompact ? 4 : 6} className="px-6 py-6 whitespace-normal">
                        <ClientBillingSheetActivityRefundDetails
                            refund={refund}
                            isCompact={isCompact}
                            onOpenOriginalPayment={onOpenOriginalPayment}
                        />
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}
